import classNames from 'classnames'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body2 } from '@ui/Typography'
import { BaseComponentProps } from '@ui/common/component.props'
import { ListItem } from '@ui/components/List'
import type { ListItemProps } from '@ui/components/List/ListItem/ListItem'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { Tooltip, TooltipContent } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { IconRenderer, IconRendererType } from '@ui/icons/utils/IconRenderer'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'

import styles from './SplitButton.module.scss'

export interface SplitButtonAction extends Pick<ListItemProps, 'disabled' | 'onClick'> {
  label: string
  icon?: IconRendererType
  disabledTooltip?: TooltipContent
}

export interface SplitButtonProps extends Omit<BaseComponentProps<HTMLDivElement>, 'children'> {
  primaryAction: SplitButtonAction
  actions: SplitButtonAction[]
  menuLabel: string
}

type MenuFocusTarget = 'first' | 'last'

export const SplitButton: React.FC<SplitButtonProps> = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { primaryAction, actions, menuLabel, ...restProps } = props
  const [isOpen, setIsOpen] = useState(false)
  const menuId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const actionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const focusedActionIndexRef = useRef<number | null>(null)
  const menuFocusTargetRef = useRef<MenuFocusTarget>('first')
  const wasOpenRef = useRef(false)
  const isFullyDisabled =
    !!primaryAction.disabled && (actions.length === 0 || actions.every((action) => action.disabled))
  const isMenuDisabled = isFullyDisabled || actions.length === 0
  const menuTooltip = isMenuDisabled ? primaryAction.disabledTooltip : t('Expand')

  const enabledActionIndexes = useMemo(
    () =>
      actions.reduce<number[]>((indexes, action, index) => {
        if (!action.disabled) indexes.push(index)
        return indexes
      }, []),
    [actions]
  )

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen

    if (isMenuDisabled) {
      setIsOpen(false)
      return
    }

    if (!isOpen) {
      focusedActionIndexRef.current = null
      return
    }

    if (enabledActionIndexes.length === 0) return

    const focusedActionIndex = focusedActionIndexRef.current
    const focusedActionDisabled =
      focusedActionIndex !== null && !enabledActionIndexes.includes(focusedActionIndex)

    if (!justOpened && !focusedActionDisabled) return

    const targetIndex =
      menuFocusTargetRef.current === 'last'
        ? enabledActionIndexes[enabledActionIndexes.length - 1]
        : enabledActionIndexes[0]

    actionRefs.current[targetIndex]?.focus()
    menuFocusTargetRef.current = 'first'
  }, [enabledActionIndexes, isMenuDisabled, isOpen])

  const moveMenuFocus = (currentIndex: number, offset: number) => {
    if (enabledActionIndexes.length === 0) return

    const currentEnabledIndex = enabledActionIndexes.indexOf(currentIndex)

    const nextEnabledIndex =
      currentEnabledIndex < 0
        ? offset > 0
          ? 0
          : enabledActionIndexes.length - 1
        : (currentEnabledIndex + offset + enabledActionIndexes.length) % enabledActionIndexes.length
    actionRefs.current[enabledActionIndexes[nextEnabledIndex]]?.focus()
  }

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = actionRefs.current.findIndex((button) => button === document.activeElement)

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveMenuFocus(currentIndex, 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        moveMenuFocus(currentIndex, -1)
        break
      case 'Home':
        event.preventDefault()
        actionRefs.current[enabledActionIndexes[0]]?.focus()
        break
      case 'End':
        event.preventDefault()
        actionRefs.current[enabledActionIndexes[enabledActionIndexes.length - 1]]?.focus()
        break
      case 'Escape':
        event.preventDefault()
        event.stopPropagation()
        setIsOpen(false)
        triggerRef.current?.focus()
        break
      case 'Tab':
        setIsOpen(false)
        break
    }
  }

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isMenuDisabled) return
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    event.preventDefault()
    menuFocusTargetRef.current = event.key === 'ArrowUp' ? 'last' : 'first'
    setIsOpen(true)
  }

  const handleSpotlightMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (primaryAction.disabled) return

    const { left, top } = event.currentTarget.getBoundingClientRect()
    event.currentTarget.style.setProperty('--split-button-spotlight-x', `${event.clientX - left}px`)
    event.currentTarget.style.setProperty('--split-button-spotlight-y', `${event.clientY - top}px`)
    event.currentTarget.style.setProperty('--split-button-spotlight-opacity', '1')
  }

  const handleSpotlightLeave = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.style.removeProperty('--split-button-spotlight-x')
    event.currentTarget.style.removeProperty('--split-button-spotlight-y')
    event.currentTarget.style.removeProperty('--split-button-spotlight-opacity')
  }

  return (
    <Box
      radius='round'
      className={classNames(styles.splitButton, {
        [styles.stateDisabled]: primaryAction.disabled,
        [styles.fullyDisabled]: isFullyDisabled,
      })}
      role='group'
      {...restProps}
    >
      <Tooltip
        content={primaryAction.disabledTooltip}
        disabled={!primaryAction.disabled || !primaryAction.disabledTooltip}
        direction='bottom'
        wrapperClassName={styles.primaryTooltip}
      >
        <button
          className={classNames(styles.action, styles.primaryAction, {
            [styles.stateDisabled]: primaryAction.disabled,
          })}
          type='button'
          disabled={primaryAction.disabled}
          onClick={primaryAction.onClick}
          onPointerMove={handleSpotlightMove}
          onPointerLeave={handleSpotlightLeave}
        >
          {primaryAction.icon && <IconRenderer icon={primaryAction.icon} size={20} />}
          <Body2 medium>{primaryAction.label}</Body2>
        </button>
      </Tooltip>

      <Box className={styles.divider} aria-hidden />

      <Box className={styles.popoverTrigger}>
        <Popover
          direction='topRight'
          distance='0.5rem'
          disabled={isMenuDisabled}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          label={menuLabel}
          contentClassName={styles.dropdownContent}
          content={
            <PopoverCard>
              <Box
                id={menuId}
                role='menu'
                aria-label={menuLabel}
                onKeyDown={handleMenuKeyDown}
                onFocusCapture={() => {
                  const index = actionRefs.current.findIndex(
                    (button) => button === document.activeElement
                  )
                  focusedActionIndexRef.current = index >= 0 ? index : null
                }}
              >
                <PopoverList>
                  {actions.map((action, index) => (
                    <Tooltip
                      key={`${action.label}-${index}`}
                      content={action.disabledTooltip}
                      disabled={!action.disabled || !action.disabledTooltip}
                      direction='bottom'
                      fullWidth
                    >
                      <ListItem
                        as='button'
                        ref={(button) => {
                          actionRefs.current[index] = button
                        }}
                        role='menuitem'
                        disabled={action.disabled}
                        tabIndex={-1}
                        title={action.label}
                        startSlot={action.icon ? { icon: action.icon } : undefined}
                        endSlot={{ icon: { icon: ArrowIcon.Right } }}
                        onClick={() => {
                          action.onClick?.()
                          setIsOpen(false)
                        }}
                      />
                    </Tooltip>
                  ))}
                </PopoverList>
              </Box>
            </PopoverCard>
          }
        >
          <Tooltip
            content={menuTooltip}
            direction='bottom'
            disabled={!menuTooltip || (isMobile && !isMenuDisabled)}
            wrapperClassName={styles.menuTriggerTooltip}
          >
            <button
              ref={triggerRef}
              className={classNames(styles.action, styles.menuTrigger, {
                [styles.selected]: isOpen,
              })}
              type='button'
              aria-label={menuLabel}
              aria-haspopup='menu'
              aria-expanded={isOpen}
              aria-controls={isOpen ? menuId : undefined}
              disabled={isMenuDisabled}
              onKeyDown={handleTriggerKeyDown}
            >
              <ChevronIcon.Down size={24} color='currentColor' />
            </button>
          </Tooltip>
        </Popover>
      </Box>
    </Box>
  )
}
